import React, { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, RefreshCw, WifiOff, Shield, FileX, CreditCard, Clock, HelpCircle, Activity, Zap, Search, Play, Phone } from 'lucide-react';
import { 
  handleDownloadError, 
  showDownloadErrorToast,
  type ErrorCategory,
  type RecoveryAction 
} from '@/lib/downloadErrorNotifications';

interface DownloadErrorNotificationProps {
  error: Error | string | null;
  context?: {
    userId?: string;
    sequenceId?: string;
    sequenceTitle?: string;
    userAgent?: string;
    ipAddress?: string;
    additionalContext?: Record<string, any>;
  };
  onRetry?: () => void;
  onDismiss?: () => void;
  isVisible?: boolean;
  showToast?: boolean; // Whether to also show a toast notification
}

const DownloadErrorNotification: React.FC<DownloadErrorNotificationProps> = ({
  error,
  context,
  onRetry,
  onDismiss,
  isVisible = true,
  showToast = false,
}) => {
  const [userResponse, setUserResponse] = useState<{
    message: string;
    description: string;
    actions: RecoveryAction[];
  } | null>(null);
  const [errorId, setErrorId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (error && isVisible) {
      processError();
    }
  }, [error, isVisible]);

  const processError = async () => {
    if (!error) return;

    setIsProcessing(true);
    try {
      const result = await handleDownloadError(error, context || {});
      setUserResponse(result.userResponse);
      setErrorId(result.errorId);

      // Show toast notification if requested
      if (showToast) {
        showDownloadErrorToast(result.userResponse, result.errorId);
      }
    } catch (processError) {
      console.error('Error processing download error:', processError);
      // Fallback response
      setUserResponse({
        message: 'Download Failed',
        description: 'An unexpected error occurred. Please try again or contact support.',
        actions: [
          {
            type: 'retry',
            label: 'Try Again',
            description: 'Retry the download'
          }
        ]
      });
      setErrorId('fallback-error');
    } finally {
      setIsProcessing(false);
    }
  };

  const getErrorIcon = (category?: ErrorCategory) => {
    switch (category) {
      case 'network':
        return <WifiOff className="h-6 w-6 text-orange-600" />;
      case 'authentication':
      case 'authorization':
        return <Shield className="h-6 w-6 text-red-600" />;
      case 'file_not_found':
        return <FileX className="h-6 w-6 text-red-600" />;
      case 'payment_required':
        return <CreditCard className="h-6 w-6 text-blue-600" />;
      case 'rate_limit':
        return <Clock className="h-6 w-6 text-yellow-600" />;
      default:
        return <AlertCircle className="h-6 w-6 text-red-600" />;
    }
  };

  const handleActionClick = (action: RecoveryAction) => {
    switch (action.type) {
      case 'retry':
        if (onRetry) {
          onRetry();
        } else if (action.action) {
          action.action();
        }
        break;
      case 'retry_with_resumption':
        // Trigger retry with resumption support
        if (onRetry) {
          onRetry();
        } else if (action.action) {
          action.action();
        }
        break;
      case 'check_network':
        if (action.url) {
          window.open(action.url, '_blank');
        }
        break;
      case 'check_dns':
        if (action.url) {
          window.open(action.url, '_blank');
        } else {
          // Default DNS check
          window.open('https://dns.google/resolve?name=google.com&type=A', '_blank');
        }
        break;
      case 'run_diagnostics':
        // Could trigger a network diagnostics modal or component
        // For now, open network status page
        window.open('chrome://network-health/', '_blank').catch(() => {
          // Fallback for non-Chrome browsers
          window.open('https://www.speedtest.net/', '_blank');
        });
        break;
      case 'contact_support':
        // Could open a support modal or navigate to support page
        window.open('mailto:lighthunt@towb.in?subject=Download Error Support&body=' + 
          encodeURIComponent(`Error ID: ${errorId}\nSequence: ${context?.sequenceTitle || 'Unknown'}\nDescription: ${userResponse?.description || 'N/A'}`), 
          '_blank'
        );
        break;
      case 'check_payment':
        // Could navigate to purchase history or payment page
        if (action.url) {
          window.open(action.url, '_blank');
        }
        break;
      case 'view_library':
        // Navigate to user's library
        window.location.href = '/library';
        break;
      case 'wait':
        // Show a waiting indicator and retry after delay
        setTimeout(() => {
          if (onRetry) {
            onRetry();
          }
        }, 30000); // 30 seconds
        break;
      case 'update_payment_method':
        // Navigate to payment method update or trigger modal
        if (action.action) {
          action.action();
        } else {
          // Default to checkout page for payment method update
          window.location.href = `/checkout/${context?.sequenceId || ''}?update_method=true`;
        }
        break;
      case 'verify_payment_info':
        // Navigate to payment verification page or modal
        if (action.action) {
          action.action();
        } else {
          // Could open a payment verification modal
          window.location.href = `/checkout/${context?.sequenceId || ''}?verify=true`;
        }
        break;
      case 'try_different_card':
        // Open checkout with different payment method option
        if (action.action) {
          action.action();
        } else {
          window.location.href = `/checkout/${context?.sequenceId || ''}?new_method=true`;
        }
        break;
      case 'contact_bank':
        // Provide bank contact guidance
        if (action.url) {
          window.open(action.url, '_blank');
        } else {
          // Show information about contacting bank
          alert('Please contact your bank or card issuer to:\n\n' +
                '• Verify the transaction\n' +
                '• Check for any blocks or restrictions\n' +
                '• Confirm your card is active\n' +
                '• Ask about international transaction settings');
        }
        break;
      default:
        if (action.action) {
          action.action();
        }
        break;
    }
  };

  if (!error || !isVisible || isProcessing) {
    return null;
  }

  if (!userResponse) {
    return (
      <Card className="w-full max-w-md mx-auto border-orange-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-6 w-6 animate-spin text-orange-600" />
            <span className="ml-2 text-sm text-muted-foreground">
              Processing error...
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const primaryAction = userResponse.actions[0];
  const secondaryActions = userResponse.actions.slice(1);

  return (
    <Card className="w-full max-w-md mx-auto border-red-200">
      <CardHeader className="text-center pb-4">
        <div className="flex justify-center mb-2">
          {getErrorIcon()}
        </div>
        <CardTitle className="text-lg text-red-700">
          {userResponse.message}
        </CardTitle>
        <CardDescription className="text-left">
          {userResponse.description}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Primary Action */}
        {primaryAction && (
          <Button 
            onClick={() => handleActionClick(primaryAction)}
            className="w-full"
            variant={primaryAction.type === 'retry' ? 'default' : 'outline-solid'}
          >
            {primaryAction.type === 'retry' && <RefreshCw className="h-4 w-4 mr-2" />}
            {primaryAction.type === 'retry_with_resumption' && <Play className="h-4 w-4 mr-2" />}
            {primaryAction.type === 'check_network' && <WifiOff className="h-4 w-4 mr-2" />}
            {primaryAction.type === 'check_dns' && <Search className="h-4 w-4 mr-2" />}
            {primaryAction.type === 'run_diagnostics' && <Zap className="h-4 w-4 mr-2" />}
            {primaryAction.type === 'contact_support' && <HelpCircle className="h-4 w-4 mr-2" />}
            {primaryAction.type === 'check_payment' && <CreditCard className="h-4 w-4 mr-2" />}
            {primaryAction.type === 'wait' && <Clock className="h-4 w-4 mr-2" />}
            {primaryAction.type === 'view_library' && <Activity className="h-4 w-4 mr-2" />}
            {primaryAction.type === 'update_payment_method' && <CreditCard className="h-4 w-4 mr-2" />}
            {primaryAction.type === 'verify_payment_info' && <Shield className="h-4 w-4 mr-2" />}
            {primaryAction.type === 'try_different_card' && <CreditCard className="h-4 w-4 mr-2" />}
            {primaryAction.type === 'contact_bank' && <Phone className="h-4 w-4 mr-2" />}
            {primaryAction.label}
          </Button>
        )}

        {/* Secondary Actions */}
        {secondaryActions.length > 0 && (
          <div className="flex flex-col space-y-2">
            {secondaryActions.map((action, index) => (
              <Button
                key={index}
                onClick={() => handleActionClick(action)}
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground hover:text-foreground"
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}

        {/* Error ID for support */}
        {errorId && (
          <Alert className="bg-muted/50">
            <AlertDescription className="text-xs">
              <strong>Error ID:</strong> {errorId.slice(0, 8)}
              <br />
              <span className="text-muted-foreground">
                Include this ID when contacting support
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Dismiss button */}
        {onDismiss && (
          <Button
            onClick={onDismiss}
            variant="ghost"
            size="sm"
            className="w-full mt-4 text-muted-foreground"
          >
            Dismiss
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default DownloadErrorNotification; 